import { useCallback, useEffect, useRef, useState } from 'react';

export type ExportFormat = 'word' | 'pdf';

export interface ExportProgressState {
  visible: boolean;
  progress: number;
  stage: string;
  format: ExportFormat;
}

const HIDDEN: ExportProgressState = { visible: false, progress: 0, stage: '', format: 'word' };

/**
 * 导出进度状态管理：
 * - start：开始导出，进度从 10% 起缓慢推进到 90%（等待服务端生成期间避免进度条停滞）
 * - setStage：服务端返回后切换到写入阶段（95%）
 * - finish：完成后进度到 100%，短暂展示后自动关闭
 * - cancel：取消/失败时立即关闭
 */
export function useExportProgress() {
  const [progressState, setProgressState] = useState<ExportProgressState>(HIDDEN);
  const timerRef = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback((format: ExportFormat, stage: string) => {
    clearTimer();
    setProgressState({ visible: true, progress: 10, stage, format });
    timerRef.current = window.setInterval(() => {
      setProgressState(prev => {
        if (!prev.visible || prev.progress >= 90) return prev;
        const step = Math.max(1, Math.round((90 - prev.progress) * 0.1));
        return { ...prev, progress: Math.min(90, prev.progress + step) };
      });
    }, 800);
  }, [clearTimer]);

  const setStage = useCallback((stage: string) => {
    setProgressState(prev => ({ ...prev, progress: 95, stage }));
  }, []);

  const finish = useCallback((stage: string) => {
    clearTimer();
    setProgressState(prev => ({ ...prev, progress: 100, stage }));
    window.setTimeout(() => setProgressState(HIDDEN), 1200);
  }, [clearTimer]);

  const cancel = useCallback(() => {
    clearTimer();
    setProgressState(HIDDEN);
  }, [clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  return { progressState, start, setStage, finish, cancel };
}
